import {
  FunctionComponent,
  PropsWithoutRef,
  forwardRef,
  Ref,
  useImperativeHandle,
  useRef,
  useCallback,
  useState,
  useLayoutEffect,
  RefObject,
} from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Flex,
  Icon,
  registerIcon,
  StyledButton,
  useI18n,
  debounce,
  defaultThemeProp,
  ForwardProps,
  OmitStrict,
} from '@pega/cosmos-react-core';
import * as caretDownIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/caret-down.icon';

import RepeatingView, {
  RepeatingViewProps,
  RepeatingViewRow,
  StyledRepeatingView,
  StyledRepeatingViewItem,
  StyledRepeatingViewUL,
} from './RepeatingView';
import Message, { MessageProps } from './Message';
import SystemMessage, { SystemMessageProps } from './SystemMessage';
import TypeIndicator, { TypeIndicatorProps } from './TypeIndicator';
import React from 'react';

registerIcon(caretDownIcon);

export interface MessageListImperativeHandleProps {
  isScrolledToLatest: () => boolean;
  scrollToLatestMessage: () => void;
  scrollToNewMessage: () => void;
}

export type MessageListItemProps =
  | (MessageProps & { type: 'message'; id: RepeatingViewRow['id'] })
  | (SystemMessageProps & { type: 'system'; id: RepeatingViewRow['id'] })
  | (TypeIndicatorProps & { type: 'typing'; id: RepeatingViewRow['id'] });

export interface MessageListProps extends OmitStrict<RepeatingViewProps, 'children' | 'rows' | 'ref'> {
  /** Messages Data array */
  messages: MessageListItemProps[];
  /** ref to the message list */
  ref?: Ref<MessageListImperativeHandleProps>;
  /** Total unread messages */
  unReadMessageCount?: number;
  /** on scroll to button */
  onScrollToButtonClick?: () => void;
}

const StyledMessageList = styled.div``;

export const NewMessageSeparatorId = 'new-message-separator';

const StyledMessageListContainer = styled.div((props) => {
  const {
    theme: {
      base: {
        shadow: { high: shadowHigh },
        colors: { white },
      },
    },
  } = props;
  return css`
    position: relative;
    overflow-y: hidden;
    > ${StyledMessageList} {
      overflow-y: auto;
    }
    > ${StyledButton} {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translate(-50%, -50%);
      box-shadow: ${shadowHigh};
      z-index: 1;
      background-color: ${white};
    }
    ${StyledRepeatingView} {
      overflow-y: hidden;
    }
    ${StyledRepeatingViewUL} {
      padding: 1rem;
    }

    ${StyledRepeatingViewItem} {
      padding: 0.125rem 0;
    }
  `;
});

StyledMessageListContainer.defaultProps = defaultThemeProp;

const ScrollToLatest: FunctionComponent<{
  scrollContainerRef: RefObject<HTMLUListElement>;
}> = (props) => {
  const { scrollContainerRef } = props;
  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight - scrollContainerRef.current.offsetHeight;
    }
  });
  return <></>;
};

const MessageList: FunctionComponent<MessageListProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<MessageListProps>, ref: MessageListProps['ref']) => {
    const { messages, unReadMessageCount = 0, onScrollToButtonClick, ...restProps } = props;

    const t = useI18n();

    const conversationRef = useRef<HTMLUListElement>(null);

    const isScrolledToLatest: MessageListImperativeHandleProps['isScrolledToLatest'] = useCallback(() => {
      return conversationRef.current
        ? conversationRef.current.scrollHeight -
            conversationRef.current.scrollTop -
            conversationRef.current.offsetHeight <=
            30
        : true;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationRef.current]);

    const scrollToLatestMessage: MessageListImperativeHandleProps['scrollToLatestMessage'] = useCallback(() => {
      if (conversationRef.current) {
        conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationRef.current]);

    const [displayScrollLatest, setDisplayScrollToLatest] = useState(false);

    const onMessageListScroll = useCallback(() => {
      setDisplayScrollToLatest(!isScrolledToLatest());
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScrolledToLatest()]);

    const scrollToNewMessage: MessageListImperativeHandleProps['scrollToNewMessage'] = useCallback(() => {
      if (conversationRef.current) {
        setDisplayScrollToLatest(false);
        const newMessageIndicatorEle =
          conversationRef.current.querySelector<HTMLElement>('[data-new-message-separator]');
        if (newMessageIndicatorEle) {
          conversationRef.current.scrollTop = newMessageIndicatorEle.offsetTop;
        } else {
          conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationRef.current]);

    useImperativeHandle<MessageListImperativeHandleProps, MessageListImperativeHandleProps>(
      ref,
      () => ({
        isScrolledToLatest,
        scrollToLatestMessage,
        scrollToNewMessage,
      }),
      [isScrolledToLatest, scrollToLatestMessage, scrollToNewMessage],
    );

    return (
      <>
        <Flex
          as={StyledMessageListContainer}
          item={{
            grow: 1,
          }}
          container={{
            direction: 'column',
          }}
          {...restProps}
        >
          {(unReadMessageCount > 0 || displayScrollLatest) && (
            <Button
              aria-label={unReadMessageCount > 0 ? t('scroll_to_unread_messages') : t('scroll_to_latest_message')}
              onClick={() => {
                if (unReadMessageCount > 0) {
                  scrollToNewMessage();
                } else {
                  scrollToLatestMessage();
                }
                onScrollToButtonClick?.();
              }}
              icon={unReadMessageCount === 0}
            >
              <Icon name="caret-down" />
              {unReadMessageCount > 0 && <>&nbsp; {t('new_messages')}</>}
            </Button>
          )}
          <RepeatingView
            as={StyledMessageList}
            container={{ direction: 'column' }}
            ref={conversationRef}
            rows={messages}
            onScroll={debounce(onMessageListScroll, 100)}
            {...restProps}
          >
            {(item: RepeatingViewRow) => {
              const { id, type, ...messageListItemRestProps } = item as MessageListItemProps;
              if (type === 'message') {
                return <Message key={id} {...(messageListItemRestProps as MessageProps)} />;
              }

              if (type === 'system') {
                const extraProps: Record<string, string> = {};
                if (id === NewMessageSeparatorId) {
                  extraProps['data-new-message-separator'] = NewMessageSeparatorId;
                }
                return <SystemMessage key={id} {...(messageListItemRestProps as SystemMessageProps)} {...extraProps} />;
              }
              if (type === 'typing') {
                return <TypeIndicator key={id} {...(messageListItemRestProps as TypeIndicatorProps)} />;
              }
            }}
          </RepeatingView>
        </Flex>
        {isScrolledToLatest() && <ScrollToLatest scrollContainerRef={conversationRef} />}
      </>
    );
  },
);

export default MessageList;
